






































































































import Vue, { PropType } from 'vue';

import { BaseOrganization } from '@/models';

export interface RoleNames {
  roleNameStudent: string | null;
  roleNameStudentPlural: string | null;
  roleNameTeacher: string | null;
  roleNameTeacherPlural: string | null;
}

/// Takes a set of derived names and org names and returns null if:
/// - The derived name matches the org name
/// - The derived name is an empty string or null
export const normalizeAgainstOrg = (
  roleNames: RoleNames,
  org: BaseOrganization
): RoleNames => {
  const nullIfEqual = (a: string | null, b: string | null): string | null => {
    if (a == null || a.trim() == '') {
      return null;
    }

    return a == b ? null : a;
  };

  return {
    roleNameStudent: nullIfEqual(
      roleNames.roleNameStudent,
      org.roleNameStudent
    ),
    roleNameStudentPlural: nullIfEqual(
      roleNames.roleNameStudentPlural,
      org.roleNameStudentPlural
    ),
    roleNameTeacher: nullIfEqual(
      roleNames.roleNameTeacher,
      org.roleNameTeacher
    ),
    roleNameTeacherPlural: nullIfEqual(
      roleNames.roleNameTeacherPlural,
      org.roleNameTeacherPlural
    )
  };
};

const rolesAreDefault = (roleNames: RoleNames): boolean => {
  const emptyOrNull = (roleName: string | null): boolean => {
    return roleName == null || roleName.trim() == '';
  };

  return [
    roleNames.roleNameStudent,
    roleNames.roleNameStudentPlural,
    roleNames.roleNameTeacher,
    roleNames.roleNameTeacherPlural
  ].every(emptyOrNull);
};

const rolesAreEqual = (a: RoleNames, b: RoleNames): boolean => {
  return (
    a.roleNameStudent === b.roleNameStudent &&
    a.roleNameStudentPlural === b.roleNameStudentPlural &&
    a.roleNameTeacher === b.roleNameTeacher &&
    a.roleNameTeacherPlural === b.roleNameTeacherPlural
  );
};

export default Vue.extend({
  components: {},

  props: {
    // The roleNames prop value is the _derived_ values
    // for role names.  We call `normalizeRoleNames` to determine
    // which of the values are inherited from the org.
    roleNames: {
      type: Object as PropType<RoleNames>,
      required: true
    },
    organization: {
      type: Object as PropType<BaseOrganization>,
      required: true
    }
  },

  data() {
    const normalizedRoleNames = normalizeAgainstOrg(
      this.roleNames,
      this.organization
    );

    return {
      showCustomInput: !rolesAreDefault(normalizedRoleNames),
      normalizedRoleNames,
      roleNamesInputFields: { ...normalizedRoleNames }
    } as {
      showCustomInput: boolean;
      normalizedRoleNames: RoleNames;
      roleNamesInputFields: RoleNames;
    };
  },

  computed: {
    initialIsDefault(): boolean {
      return rolesAreDefault(this.normalizedRoleNames);
    },

    inputFieldsAreDefault(): boolean {
      return rolesAreDefault(this.roleNamesInputFields);
    },

    unchanged(): boolean {
      // Accounts for fields changing from null -> ''
      if (this.initialIsDefault && this.inputFieldsAreDefault) {
        return true;
      }

      if (rolesAreEqual(this.roleNamesInputFields, this.normalizedRoleNames)) {
        return true;
      }

      return false;
    },

    /// We want to change '' -> null on the output model, as well as any names
    /// that match the org default?
    outputModel(): RoleNames {
      return normalizeAgainstOrg(this.roleNamesInputFields, this.organization);
    },

    derivedNames(): RoleNames {
      return {
        roleNameStudent:
          this.roleNamesInputFields.roleNameStudent?.trim() ||
          this.organization.roleNameStudent,
        roleNameStudentPlural:
          this.roleNamesInputFields.roleNameStudentPlural?.trim() ||
          this.organization.roleNameStudentPlural,
        roleNameTeacher:
          this.roleNamesInputFields.roleNameTeacher?.trim() ||
          this.organization.roleNameTeacher,
        roleNameTeacherPlural:
          this.roleNamesInputFields.roleNameTeacherPlural?.trim() ||
          this.organization.roleNameTeacherPlural
      };
    }
  },

  methods: {
    // If we're either enabling or disabling custom input, we
    // need to clear the model.
    clearModel() {
      // If we're either enabling or disabling custom input, we
      // need to clear the model.
      this.roleNamesInputFields = {
        roleNameStudent: '',
        roleNameStudentPlural: '',
        roleNameTeacher: '',
        roleNameTeacherPlural: ''
      };

      this.notifyChanged();
    },

    notifyChanged() {
      const event = {
        dirty: !this.unchanged,
        roleNames: this.outputModel
      };

      this.$emit('change', event);
    }
  }
});
